/**
 * SMS Message Text Admin
 * ----------------------
 * Lets an SMS admin edit the wording of booking messages. The text is stored
 * server-side, so a change made here is seen by every user.
 *
 * The reply keywords (Yes / No / Call / Text) are intentionally not editable:
 * the backend matches inbound replies against them, so changing them here
 * would silently break the conversation flow. Their descriptions are editable.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useMsal } from '@azure/msal-react';
import { isSmsAdminAccount } from '../utils/smsPermissions';
import messageConfigService, {
  AVAILABLE_PLACEHOLDERS,
  CONFIGURABLE_TYPE_IDS,
  DEFAULT_CONFIG
} from '../services/smsMessageConfigService';

const TYPE_LABELS = {
  WHA: 'Whole House Assessment',
  EPC: 'Energy Performance Certificate',
  POST_EPC: 'Post-installation EPC',
  POST_QI: 'Post-installation quality inspection'
};

/** GSM-7 vs UCS-2 segment maths, so admins can see the real cost of a message. */
const UNICODE_PATTERN = /[^ -£§à-ü\n\r]/;

const smsSegments = (text) => {
  const value = String(text || '');
  const length = value.length;
  if (length === 0) return { length: 0, segments: 0, encoding: 'GSM-7' };

  const isUnicode = UNICODE_PATTERN.test(value);
  const single = isUnicode ? 70 : 160;
  const multi = isUnicode ? 67 : 153;
  const segments = length <= single ? 1 : Math.ceil(length / multi);

  return { length, segments, encoding: isUnicode ? 'UCS-2' : 'GSM-7' };
};

/** Replace placeholders with sample values so the preview reads naturally. */
const renderPreview = (template) => {
  let output = String(template || '');
  AVAILABLE_PLACEHOLDERS.forEach(({ token, sample }) => {
    output = output.replaceAll(token, sample);
  });
  return output;
};

/** Flag any {{TOKEN}} that is not one we know how to substitute. */
const findUnknownPlaceholders = (text) => {
  const known = new Set(AVAILABLE_PLACEHOLDERS.map((p) => p.token));
  const found = String(text || '').match(/\{\{[A-Z0-9_]+\}\}/g) || [];
  return [...new Set(found.filter((token) => !known.has(token)))];
};

function SegmentBadge({ text }) {
  const { length, segments, encoding } = smsSegments(text);
  const variant = segments <= 2 ? 'secondary' : segments <= 4 ? 'warning' : 'danger';
  return (
    <Badge bg={variant} className="ms-2" title={`${encoding} encoding`}>
      {length} chars &middot; {segments} SMS
    </Badge>
  );
}

function PlaceholderButtons({ onInsert }) {
  return (
    <div className="d-flex flex-wrap gap-1 mb-2">
      {AVAILABLE_PLACEHOLDERS.map(({ token, label }) => (
        <Button
          key={token}
          size="sm"
          variant="outline-secondary"
          className="py-0 px-2"
          style={{ fontSize: '0.75rem' }}
          title={`Insert ${label}`}
          onClick={() => onInsert(token)}
        >
          {token}
        </Button>
      ))}
    </div>
  );
}

function SMSMessageAdmin() {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount() || instance.getAllAccounts?.()[0] || null;
  const isAdmin = isSmsAdminAccount(activeAccount);

  const [config, setConfig] = useState(() => messageConfigService.withDefaults(null));
  const [savedConfig, setSavedConfig] = useState(() => messageConfigService.withDefaults(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isStored, setIsStored] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '' });
  const [activeType, setActiveType] = useState('WHA');
  const [showConfirm, setShowConfirm] = useState(false);

  const bodyRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { config: loaded, isStored: stored, error } = await messageConfigService.loadConfig(instance);
    setConfig(loaded);
    setSavedConfig(loaded);
    setIsStored(stored);
    setLoading(false);

    if (error) {
      setAlert({
        type: 'warning',
        message: `${error} Showing the built-in wording — saving will create a fresh stored copy.`
      });
    } else if (!stored) {
      setAlert({
        type: 'info',
        message: 'No saved wording yet, so the built-in text is shown. Save to make it editable for everyone.'
      });
    } else {
      setAlert({ type: '', message: '' });
    }
  }, [instance]);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  const isDirty = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(savedConfig),
    [config, savedConfig]
  );

  // Warn before losing edits on tab close or refresh.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const setShared = useCallback((key, value) => {
    setConfig((prev) => ({ ...prev, shared: { ...prev.shared, [key]: value } }));
  }, []);

  const setTypeBody = useCallback((typeId, value) => {
    setConfig((prev) => ({
      ...prev,
      types: { ...prev.types, [typeId]: { ...prev.types[typeId], body: value } }
    }));
  }, []);

  /** Insert a placeholder at the cursor rather than appending to the end. */
  const insertPlaceholder = useCallback((token) => {
    const field = bodyRef.current;
    const current = config.types[activeType]?.body || '';
    if (!field) {
      setTypeBody(activeType, `${current}${token}`);
      return;
    }
    const start = field.selectionStart ?? current.length;
    const end = field.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    setTypeBody(activeType, next);
    requestAnimationFrame(() => {
      field.focus();
      field.setSelectionRange(start + token.length, start + token.length);
    });
  }, [activeType, config.types, setTypeBody]);

  const fullMessage = useMemo(
    () => messageConfigService.buildOutboundTemplate(config, activeType),
    [config, activeType]
  );

  const offerMessage = useMemo(
    () => messageConfigService.buildOfferTemplate(config),
    [config]
  );

  const unknownTokens = useMemo(
    () => findUnknownPlaceholders(`${fullMessage}\n${offerMessage}`),
    [fullMessage, offerMessage]
  );

  const handleSave = useCallback(async () => {
    setShowConfirm(false);
    setSaving(true);
    const { ok, error } = await messageConfigService.saveConfig(
      config,
      instance,
      activeAccount?.username || 'unknown'
    );
    setSaving(false);

    if (ok) {
      setSavedConfig(config);
      setIsStored(true);
      setAlert({ type: 'success', message: 'Saved. All users will see this wording on their next page load.' });
    } else {
      setAlert({ type: 'danger', message: error });
    }
  }, [config, instance, activeAccount]);

  const handleResetType = useCallback(() => {
    setTypeBody(activeType, DEFAULT_CONFIG.types[activeType].body);
  }, [activeType, setTypeBody]);

  const handleDiscard = useCallback(() => {
    setConfig(savedConfig);
    setAlert({ type: '', message: '' });
  }, [savedConfig]);

  if (!isAdmin) {
    return (
      <Alert variant="secondary" className="mt-3">
        You need SMS admin rights to change message wording.
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading message wording…
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
        <div>
          <h5 className="mb-1">Message Text</h5>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Wording used for booking SMS messages. Changes are saved centrally and apply to everyone.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {isDirty && <Badge bg="warning" text="dark">Unsaved changes</Badge>}
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={saving}>
            Reload
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={handleDiscard} disabled={!isDirty || saving}>
            Discard
          </Button>
          <Button variant="primary" size="sm" onClick={() => setShowConfirm(true)} disabled={!isDirty || saving}>
            {saving ? <><Spinner as="span" animation="border" size="sm" className="me-1" />Saving…</> : 'Save for everyone'}
          </Button>
        </div>
      </div>

      {savedConfig.updatedAt && (
        <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
          Last changed {new Date(savedConfig.updatedAt).toLocaleString('en-GB')}
          {savedConfig.updatedBy ? ` by ${savedConfig.updatedBy}` : ''}
          {!isStored && ' (not yet stored)'}
        </p>
      )}

      {alert.message && (
        <Alert variant={alert.type || 'info'} dismissible onClose={() => setAlert({ type: '', message: '' })}>
          {alert.message}
        </Alert>
      )}

      {unknownTokens.length > 0 && (
        <Alert variant="danger">
          <strong>Unrecognised placeholder{unknownTokens.length > 1 ? 's' : ''}:</strong>{' '}
          {unknownTokens.join(', ')} — these will be sent to the customer as literal text.
          Use only the placeholder buttons below.
        </Alert>
      )}

      <Row>
        {/* ------------------------- Editing ------------------------- */}
        <Col lg={7}>
          <Card className="mb-3">
            <Card.Header className="py-2"><strong>Appointment message</strong></Card.Header>
            <Card.Body>
              <Tabs
                activeKey={activeType}
                onSelect={(key) => key && setActiveType(key)}
                className="mb-3"
              >
                {CONFIGURABLE_TYPE_IDS.map((typeId) => (
                  <Tab key={typeId} eventKey={typeId} title={TYPE_LABELS[typeId]} />
                ))}
              </Tabs>

              <Form.Label className="mb-1">
                Message body
                <SegmentBadge text={renderPreview(fullMessage)} />
              </Form.Label>
              <PlaceholderButtons onInsert={insertPlaceholder} />
              <Form.Control
                as="textarea"
                ref={bodyRef}
                rows={9}
                value={config.types[activeType]?.body || ''}
                onChange={(event) => setTypeBody(activeType, event.target.value)}
                style={{ fontSize: '0.875rem' }}
              />
              <div className="d-flex justify-content-between mt-1">
                <Form.Text muted>
                  The reply instructions below are added automatically to the end of this message.
                </Form.Text>
                <Button variant="link" size="sm" className="p-0" onClick={handleResetType}>
                  Reset this one to original
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="py-2">
              <strong>Reply instructions</strong>
              <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                shared by all appointment types
              </span>
            </Card.Header>
            <Card.Body>
              <Alert variant="light" className="py-2 mb-3" style={{ fontSize: '0.8rem' }}>
                The words <strong>Yes</strong>, <strong>No</strong>, <strong>Call</strong> and{' '}
                <strong>Text</strong> cannot be changed — the system matches customer replies
                against them. You can change what each one says it does.
              </Alert>

              <Form.Group className="mb-2">
                <Form.Label className="mb-1" style={{ fontSize: '0.875rem' }}>Introduction line</Form.Label>
                <Form.Control
                  size="sm"
                  value={config.shared.replyIntro}
                  onChange={(event) => setShared('replyIntro', event.target.value)}
                />
              </Form.Group>

              {[
                ['Yes', 'replyYes'],
                ['No', 'replyNo'],
                ['Call', 'replyCall'],
                ['Text', 'replyText']
              ].map(([keyword, field]) => (
                <Form.Group as={Row} className="mb-2 align-items-center" key={field}>
                  <Form.Label column sm={2} className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {keyword} –
                  </Form.Label>
                  <Col sm={10}>
                    <Form.Control
                      size="sm"
                      value={config.shared[field]}
                      onChange={(event) => setShared(field, event.target.value)}
                    />
                  </Col>
                </Form.Group>
              ))}

              <Form.Group className="mb-2">
                <Form.Label className="mb-1" style={{ fontSize: '0.875rem' }}>Closing line</Form.Label>
                <Form.Control
                  size="sm"
                  value={config.shared.replyClosing}
                  onChange={(event) => setShared('replyClosing', event.target.value)}
                />
              </Form.Group>

              <Form.Group>
                <Form.Label className="mb-1" style={{ fontSize: '0.875rem' }}>
                  Office hours
                  <span className="text-muted ms-2" style={{ fontSize: '0.75rem' }}>
                    used everywhere {'{{OFFICE_HOURS}}'} appears — change once, applies to all messages
                  </span>
                </Form.Label>
                <Form.Control
                  size="sm"
                  value={config.shared.officeHours}
                  onChange={(event) => setShared('officeHours', event.target.value)}
                />
              </Form.Group>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="py-2"><strong>Alternative appointment offer</strong></Card.Header>
            <Card.Body>
              <Form.Control
                as="textarea"
                rows={3}
                value={config.shared.offerBody}
                onChange={(event) => setShared('offerBody', event.target.value)}
                style={{ fontSize: '0.875rem' }}
              />
              <Form.Text muted>
                Sent when a customer replies No and a second or third date was offered.
              </Form.Text>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="py-2"><strong>Replies to the customer</strong></Card.Header>
            <Card.Body>
              {[
                ['yesResponse', 'When they reply Yes'],
                ['noFinalResponse', 'When they reply No and no dates are left'],
                ['callResponse', 'When they reply Call'],
                ['textResponse', 'When they reply Text'],
                ['invalidResponse', 'When the reply is not recognised']
              ].map(([field, label]) => (
                <Form.Group className="mb-3" key={field}>
                  <Form.Label className="mb-1" style={{ fontSize: '0.875rem' }}>
                    {label}
                    <SegmentBadge text={renderPreview(config.shared[field])} />
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={config.shared[field]}
                    onChange={(event) => setShared(field, event.target.value)}
                    style={{ fontSize: '0.875rem' }}
                  />
                </Form.Group>
              ))}
            </Card.Body>
          </Card>
        </Col>

        {/* ------------------------- Preview ------------------------- */}
        <Col lg={5}>
          <div style={{ position: 'sticky', top: '1rem' }}>
            <Card className="mb-3">
              <Card.Header className="py-2">
                <strong>Preview</strong>
                <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                  with example details
                </span>
              </Card.Header>
              <Card.Body>
                <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                  {TYPE_LABELS[activeType]} — first message
                  <SegmentBadge text={renderPreview(fullMessage)} />
                </p>
                <div
                  className="p-3 mb-3"
                  style={{
                    background: '#e7f3ff',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.5
                  }}
                >
                  {renderPreview(fullMessage)}
                </div>

                <p className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
                  Alternative date offer
                  <SegmentBadge text={renderPreview(offerMessage)} />
                </p>
                <div
                  className="p-3"
                  style={{
                    background: '#f1f3f5',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.5
                  }}
                >
                  {renderPreview(offerMessage)}
                </div>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>Save wording for everyone?</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.9rem' }}>
          <p>
            This wording will be used for all booking messages sent from now on, by every user,
            and goes to real customers.
          </p>
          <p className="mb-2"><strong>Check the preview reads correctly before saving.</strong></p>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Messages currently in progress are unaffected.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirm(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave}>Save</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default SMSMessageAdmin;
